import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useMediaQueries } from '@honda-canada/design-system-react';
import gtmForGenericBapEvent from '../../../core/gtm/gtmForGenericBapEvent';
import isSSR from '../../../utils/isSSR';
import useCheckConfigureBapSectionAvailability from './useCheckConfigureBapSectionAvailability';
import useFetchColors from './useFetchColors';
import useInventoryStatus from './useInventoryStatus';
import useProtectionData from './useProtectionData';
import { ACCESSORIES, CHARGING, EXTERIOR, INTERIOR, PROTECTION, WHEELS } from '../utils/constants';
import { selectBodyTypesForGtm, selectCurrentVehicleFromVehicles, selectTransmissionFromCurrentVehicle, } from '../../../core/reducers/vehicles';
import { selectCurrentDrawerState, setActiveProgressBarItem } from '../../../core/reducers/ui';
import { useAccessoriesFetch } from '../../../core/apiHooks/useAccessoriesFetch';
import { useAppSelector } from '../../../core/utils';
export const progressBarSections = [EXTERIOR, WHEELS, INTERIOR, CHARGING, ACCESSORIES, PROTECTION];
// in the ssr app, there are several untraced calls windows.scrollTo when on the configure page.
// these have been the scroll disruptor we've been racking our heads over
// this hook temporarily overrides the default window.scrollTo method
const useInterceptWindowScrollControls = () => {
    const windowScrollTo = useRef(null);
    useEffect(() => {
        if (isSSR())
            return;
        windowScrollTo.current = window.scrollTo.bind(window);
        window.scrollTo = () => { };
        return () => {
            if (!windowScrollTo.current)
                return;
            window.scrollTo = windowScrollTo.current;
        };
    }, []);
    return windowScrollTo;
};
const useVisibleSections = () => {
    var _a, _b;
    const dispatch = useDispatch();
    const { isDesktop } = useMediaQueries();
    const location = useLocation();
    const hash = location.hash.replace('#', '').toLowerCase();
    const [visibleSections, setVisibleSections] = useState([]);
    const activeProgressBarItem = visibleSections[0] || '';
    const [prevActiveProgressBarItem, setPrevActiveProgressBarItem] = useState('');
    const [userScrollForSideBarDetected, setUserScrollForSideBarDetected] = useState(false);
    const currentDrawerState = useAppSelector(selectCurrentDrawerState);
    const windowScrollTo = useInterceptWindowScrollControls();
    const gtmBodyStyle = useAppSelector(selectBodyTypesForGtm);
    const { inventoryStatus } = useInventoryStatus();
    const currentTransmission = useAppSelector(selectTransmissionFromCurrentVehicle);
    const currentVehicle = useAppSelector(selectCurrentVehicleFromVehicles);
    const { isSuccess: hasAccessoriesLoadedSuccessfully, error: hasAccessoriesLoadFailed, data: accessoriesData, } = useAccessoriesFetch((currentTransmission === null || currentTransmission === void 0 ? void 0 : currentTransmission.detIdentifier) || '');
    const protectionData = useProtectionData();
    const { isDataLoaded: hasProtectionDataLoaded, isDataFailed: hasProtectionDataFailed } = protectionData;
    const { isSuccess: hasColorLoadedSuccessfully, hasError: hasColorLoadFailed } = useFetchColors((_a = currentVehicle === null || currentVehicle === void 0 ? void 0 : currentVehicle.modelSitecoreId) !== null && _a !== void 0 ? _a : '', (_b = currentVehicle === null || currentVehicle === void 0 ? void 0 : currentVehicle.modelYear) !== null && _b !== void 0 ? _b : '');
    const checkConfigureBapSectionAvailability = useCheckConfigureBapSectionAvailability(protectionData, accessoriesData);
    const isColorsDataProcessed = hasColorLoadedSuccessfully || hasColorLoadFailed;
    const isProtectionDataProcessed = hasProtectionDataLoaded || hasProtectionDataFailed;
    const isAccessoriesDataProcessed = hasAccessoriesLoadedSuccessfully || hasAccessoriesLoadFailed;
    const isAllDataProcessed = isColorsDataProcessed && isProtectionDataProcessed && isAccessoriesDataProcessed;
    const scrollTarget = useRef('');
    const [scrollTrigger, updateScrollTrigger] = useState(false);
    const scrollTriggerPrevState = useRef(scrollTrigger);
    // causes the scroll effect hook to run
    const triggerScrollSignal = () => {
        updateScrollTrigger(prev => {
            scrollTriggerPrevState.current = prev;
            return !prev;
        });
    };
    // prevents the scroll effect hook from running
    const blockScrollSignal = () => {
        updateScrollTrigger(scrollTriggerPrevState.current);
    };
    const [observerQueue, setObserverQueue] = useState([]);
    const handleInViewChange = useCallback((inView, sectionName, isSectionHidden) => {
        if (!isAllDataProcessed) {
            setObserverQueue(prev => [...prev, [inView, sectionName, isSectionHidden]]);
            return;
        }
        setVisibleSections(prev => {
            const sections = new Set(prev);
            const isSectionAvailable = checkConfigureBapSectionAvailability(sectionName);
            if (inView && !isSectionHidden && isSectionAvailable) {
                sections.add(sectionName);
            }
            else {
                sections.delete(sectionName);
            }
            return progressBarSections.filter(section => sections.has(section));
        });
    }, [isAllDataProcessed]);
    const isObserverReadyRef = useRef(false);
    // this effect reruns all observer callbacks that were blocked while isAllDataProcessed was false,
    // in order to preserve all initial intersection states and get accurate results from future handleInViewChange calls
    useEffect(() => {
        if (!isAllDataProcessed || isObserverReadyRef.current)
            return;
        isObserverReadyRef.current = true;
        if (hash) {
            setObserverQueue([]);
            return;
        }
        // eslint-disable-next-line no-restricted-syntax
        for (const args of observerQueue) {
            handleInViewChange(...args);
        }
        setObserverQueue([]);
    }, [isAllDataProcessed, handleInViewChange, observerQueue, hash]);
    useEffect(() => {
        if (!activeProgressBarItem || !isAllDataProcessed)
            return;
        dispatch(setActiveProgressBarItem(activeProgressBarItem));
    }, [dispatch, activeProgressBarItem, isAllDataProcessed]);
    const scheduledHash = useRef('');
    // This effect listens for hash changes and then triggers a scroll to the section corresponding to the hash
    useEffect(() => {
        if (!hash)
            return;
        if (!isAllDataProcessed) {
            if (!scheduledHash.current)
                scheduledHash.current = hash;
            return;
        }
        let targetHash = hash;
        if (scheduledHash.current) {
            targetHash = scheduledHash.current;
            scheduledHash.current = '';
        }
        scrollTarget.current = targetHash;
        triggerScrollSignal();
    }, [hash, isAllDataProcessed]);
    const scrollSectionIntoView = useCallback((sectionName) => {
        var _a, _b;
        const element = document.querySelector(`#${sectionName}-${isDesktop ? 'section' : 'title'}`);
        if (!element)
            return;
        const scrollPosition = EXTERIOR === sectionName ? 'center' : 'start';
        if (isDesktop) {
            element.scrollIntoView({ block: scrollPosition });
        }
        else if (currentDrawerState === 'half') {
            element.scrollIntoView({ block: scrollPosition });
            (_a = windowScrollTo.current) === null || _a === void 0 ? void 0 : _a.call(windowScrollTo, 0, 0);
        }
        else {
            const scrollOffset = 120;
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.scrollY - scrollOffset;
            (_b = windowScrollTo.current) === null || _b === void 0 ? void 0 : _b.call(windowScrollTo, {
                top: offsetPosition,
            });
        }
    }, [isDesktop, currentDrawerState, windowScrollTo]);
    // This is the scroll effect hook, triggered/blocked by triggerScrollSignal/blockScrollSignal
    // regardless of what other dependencies change
    useEffect(() => {
        if (!scrollTarget.current || scrollTrigger === scrollTriggerPrevState.current)
            return;
        blockScrollSignal();
        const targetSection = scrollTarget.current;
        scrollTarget.current = '';
        scrollSectionIntoView(targetSection);
    }, [scrollTrigger, scrollSectionIntoView]);
    useEffect(() => {
        const onScroll = () => {
            if (prevActiveProgressBarItem) {
                setUserScrollForSideBarDetected(true);
            }
        };
        window.addEventListener('scroll', onScroll);
        return () => {
            window.removeEventListener('scroll', onScroll);
        };
    }, [prevActiveProgressBarItem]);
    useLayoutEffect(() => {
        if (!activeProgressBarItem || !isAllDataProcessed)
            return;
        // TODO: move gtm to event handler
        if (prevActiveProgressBarItem !== activeProgressBarItem && userScrollForSideBarDetected) {
            gtmForGenericBapEvent({
                model: currentVehicle === null || currentVehicle === void 0 ? void 0 : currentVehicle.modelGtmName,
                trim: currentVehicle === null || currentVehicle === void 0 ? void 0 : currentVehicle.trimGtmName,
                eventName: 'customization_scroll',
                gtmBodyStyle,
                extraPayload: {
                    section_name: activeProgressBarItem,
                    availability_flag: inventoryStatus,
                },
            });
        }
        // FIXME: prevActiveProgressBarItem was missing from deps array, but adding it trigger two updates
        setPrevActiveProgressBarItem(activeProgressBarItem);
    }, [
        dispatch,
        isAllDataProcessed,
        prevActiveProgressBarItem,
        userScrollForSideBarDetected,
        currentVehicle === null || currentVehicle === void 0 ? void 0 : currentVehicle.modelGtmName,
        currentVehicle === null || currentVehicle === void 0 ? void 0 : currentVehicle.trimGtmName,
        gtmBodyStyle,
        inventoryStatus,
        activeProgressBarItem,
    ]);
    return {
        handleInViewChange,
        scrollSectionIntoView,
    };
};
export default useVisibleSections;
